import { useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useState, useEffect } from 'react';
import { isLogged } from '@utils/auth';
import { SIGNIN_URL } from '@data/constants';
import { useUserContext } from '@contexts/UserContext';
import { useAuthContext } from '@contexts/AuthContext';
import * as Sentry from '@sentry/react';

import { ROLE_ADMIN, ROLE_VOLUNTEER, ROLE_ASSESSOR } from '@services/users';

const ProtectedRoute = ({
  element,
  allowedRoles,
}: {
  element: React.ReactNode;
  allowedRoles?: string[];
}) => {
  const [searchParams] = useSearchParams();
  const { user } = useUserContext();
  const { loginWithCode, isAuthenticated } = useAuthContext();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function login() {
      const code = searchParams.get('code');

      if (!code) {
        setLoading(false);
      }

      if (code && code !== Cookies.get('used_code')) {
        Cookies.set('used_code', code);
        await loginWithCode(code);
        setLoading(false);
      } else if (isLogged()) {
        setLoading(false);
      }
    }

    login().catch((error) => {
      setLoading(false);
      console.error(error);
      Sentry.captureException(error);
      window.location.href = '/user/error';
    });
  }, [searchParams, isAuthenticated, loginWithCode]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!isLogged()) {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      window.location.href = `${SIGNIN_URL}?redirect_url=${url.toString()}`;
    }
  }, [loading]);

  useEffect(() => {
    if (allowedRoles && user && !allowedRoles.includes(user.role)) {
      if (user.role === ROLE_VOLUNTEER) {
        window.location.href = '/user/profile';
      }

      if (user.role === ROLE_ADMIN || user.role === ROLE_ASSESSOR) {
        window.location.href = '/user/admin';
      }
    }
  }, [user, allowedRoles]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isLogged()) {
    return null;
  }

  if (allowedRoles && !user) {
    return null;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return null;
  }

  return element;
};

export default ProtectedRoute;
